import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Minus } from "lucide-react";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { useStudioList } from "@/lib/useStudioList";
import { money } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const Trend = ({ now, prev }) => {
  if (prev == null) return <Minus className="inline h-3.5 w-3.5 text-slate-400" />;
  if (now > prev) return <ArrowUp className="inline h-3.5 w-3.5 text-emerald-600" />;
  if (now < prev) return <ArrowDown className="inline h-3.5 w-3.5 text-rose-600" />;
  return <Minus className="inline h-3.5 w-3.5 text-slate-400" />;
};

export default function StudioPerformance({ onOpen, rows: rowsProp }) {
  const { rows: fetched } = useStudioList(!rowsProp);
  const rows = rowsProp || fetched;
  const [open, setOpen] = useState(null);
  const paged = usePaged(rows);

  return (
    <div>
      <PageHead
        title="Studio Performance"
        subtitle="B2C service performance per studio · current month"
      />
      <div className="space-y-3" data-testid="studio-performance-list">
        {paged.visible.map((s) => {
          const expanded = open === s.studio_id;
          return (
            <Card key={s.studio_id} className="border-brand-line shadow-none">
              <CardContent className="p-0">
                <button
                  data-testid={`perf-row-${s.studio_id}`}
                  onClick={() => setOpen(expanded ? null : s.studio_id)}
                  className="flex w-full flex-wrap items-center justify-between gap-4 p-5 text-left transition-colors duration-200 hover:bg-brand-tint/60"
                >
                  <div className="flex min-w-[220px] items-center gap-2">
                    {expanded ? (
                      <ChevronDown className="h-4 w-4 text-crimson-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-brand/50" />
                    )}
                    <div>
                      <p className="font-display text-base font-semibold text-brand-ink">{s.name}</p>
                      <p className="text-xs text-slate-500">
                        {s.city} · {s.subscription_status}
                      </p>
                    </div>
                  </div>
                  <Metric label="B2C revenue (mo)" value={money(s.b2c_revenue_month)} testid={`perf-revenue-${s.studio_id}`} />
                  <Metric label="Last month" value={money(s.b2c_revenue_prev_month)} />
                  <Metric
                    label="B2C profit (mo)"
                    value={money(s.b2c_profit_month)}
                    tone="text-emerald-600"
                    testid={`perf-profit-${s.studio_id}`}
                  />
                  <Metric label="Jobs / avg ticket" value={`${s.b2c_jobs_month} · ${money(s.avg_ticket)}`} testid={`perf-jobs-${s.studio_id}`} />
                  <Metric label="Tier readiness" value={(s.tier_readiness ?? 0).toFixed(1)} tone="text-crimson-600" testid={`perf-tier-${s.studio_id}`} />
                  <Metric
                    label="Reminders"
                    value={
                      <Badge
                        data-testid={`perf-reminders-${s.studio_id}`}
                        className={`border-0 ${s.pending_reminders > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}
                      >
                        {s.pending_reminders}
                      </Badge>
                    }
                  />
                </button>

                {expanded && (
                  <div className="border-t border-brand-line bg-slate-50/60 p-5" data-testid={`perf-panel-${s.studio_id}`}>
                    <div className="flex flex-wrap gap-6 text-sm text-slate-600">
                      <span>Target progress: {s.revenue_progress_pct}%</span>
                      <span>Low stock SKUs: {s.low_stock}</span>
                      <span>Customers: {s.customers}</span>
                    </div>
                    <button
                      data-testid={`perf-open-${s.studio_id}`}
                      onClick={() => onOpen(s.studio_id)}
                      className="mt-4 rounded bg-brand px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      Open full studio detail
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        <ShowMore paged={paged} testid="studio-performance-show-more" />
      </div>
    </div>
  );
}

const Metric = ({ label, value, tone, testid }) => (
  <div className="min-w-[120px]">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-brand/60">{label}</p>
    <div data-testid={testid} className={`mt-0.5 text-sm font-semibold ${tone || "text-brand-ink"}`}>
      {value}
    </div>
  </div>
);
